import React, { useMemo } from 'react';
import { Alert, Col, Row } from 'react-bootstrap-5';

import UserList from './user_list';
import ToggleOverrideSection from '../shared/toggle_override_section';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/pro-solid-svg-icons';
import { isEqual } from 'lodash';

interface User {
  id: number;
  name: string;
}

interface UserPickerProps {
  companyId?: number;
  defaultUserIds?: number[] | null;
  selectAllUserIds?: boolean;
  selectedUserIds?: number[] | null;
  users?: User[];
  onChange: (payload: { userIds?: number[], selectAllUserIds?: boolean }) => void;
}

export default function UserPicker({
  companyId,
  defaultUserIds,
  selectAllUserIds,
  selectedUserIds = [],
  onChange
}: UserPickerProps) {
  const handleAddUser = (id: number) => {
    const userIds = [...(selectedUserIds || []), id];
    onChange({ userIds });
  };

  const handleRemoveUser = (id: number) => {
    const userIds = [...(selectedUserIds || [])].filter(userId => userId !== id);
    onChange({ userIds });
  };

  const handleSetDefaults = () => {
    onChange({ selectAllUserIds: false, userIds: defaultUserIds || [] });
  };

  const canSetDefault : boolean = useMemo(() => {
    return !!defaultUserIds && defaultUserIds.length > 0 &&
           !selectAllUserIds && !isEqual(defaultUserIds?.sort(), selectedUserIds?.sort());
  }, [defaultUserIds, selectAllUserIds, selectedUserIds]);

  const renderDefaultAlert = () => {
    if (!canSetDefault) return null;

    return (
      <Alert className="text-center" variant="info">
        <div>
          The users selected have been edited from the initial configuration.
          Selecting "revert" below will undo those changes,
          and make this document visible to those selected when this template was created.
        </div>
        <div className="btn btn-ghost btn-sm mt-1" onClick={handleSetDefaults}>
          <FontAwesomeIcon icon={faUndo} />
          Revert Available Users
        </div>
      </Alert>
    );
  };

  return (
    <div>
      <ToggleOverrideSection
        checked={selectAllUserIds}
        name="user-ids-override"
        subtitle={selectAllUserIds ?
          'Unchecking will allow you to grant access to this document template on a per-user basis' :
          'Checking will grant access to this document template for all users'
        }
        title="Enable for all users"
        onOverride={handleSetDefaults}
        onRemove={() => onChange({ selectAllUserIds: true })}
      />
      {renderDefaultAlert()}
      {!selectAllUserIds && (
        <Row>
          <Col md={6}>
            <div className="my-3">
              <strong className="fw-semibold">All Available Users</strong>
            </div>
            <UserList companyId={companyId} excludeUserIds={selectedUserIds || []} onAddUser={handleAddUser}/>
          </Col>
          <Col md={6}>
            <div className="my-3">
              <strong className="fw-semibold">Users with Access</strong>
            </div>
            <UserList companyId={companyId} includeUserIds={selectedUserIds || []} onRemoveUser={handleRemoveUser}/>
          </Col>
        </Row>
      )}
    </div>
  );
}
